<template>
  <v-dialog v-model="showModalInternal" max-width="600">
    <v-card>
      <v-card-title>{{ trans(isUpdate ? 'misc.recurring.editMisc' : 'misc.recurring.newMisc') }}</v-card-title>
      <v-divider/>
      <v-card-text>
        <global-validation-errors class="mt-4" :value="validationErrors"/>
        <v-row class="mt-2">
          <v-col cols="7">
            <select-object
              :error-messages="validationErrors['objectId']"
              :object-id.sync="recurringMisc.objectId"
            ></select-object>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <select-misc-account
              :misc-account-id.sync="recurringMisc.miscAccountId"
            ></select-misc-account>
          </v-col>
          <v-col cols="6">
            <v-text-field
              :label="trans('misc.receiptField')"
              v-model="recurringMisc.receiptField"
              class="mt-0"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-text-field
              :label="trans('misc.recurring.descriptionTemplate')"
              v-model="recurringMisc.descriptionTemplate"
              :error-messages="validationErrors['descriptionTemplate']"
            ></v-text-field>
            <template-placeholder-list dense :placeholders="placeholdersDescriptionTemplate" class="mb-5" style="font-size: 0.95em;" />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              v-model="recurringMisc.interval"
              :label="trans('misc.recurring.interval')"
              :items="recurringMiscIntervalOptions"
            ></v-select>
          </v-col>
          <v-col>
            <date-select
              v-model="recurringMisc.from"
              type="month"
              :label="trans('misc.recurring.from')"
            />
          </v-col>
          <v-col>
            <date-select
              v-model="recurringMisc.to"
              type="month"
              :label="trans('misc.recurring.to')"
            />
            <div class="subtext mt-1">{{ trans('general.optional') }}</div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <currency-input
              :error-messages="validationErrors['price']"
              :label="trans('misc.priceGross')"
              v-model="recurringMisc.price"
              currency="EUR"
              @change="recurringMisc.priceNet = null"
              :allow-only-negative="true"
            ></currency-input>
          </v-col>
          <v-col cols="6">
            <currency-input
              :label="trans('misc.taxIfAvailable')"
              v-model="recurringMisc.tax"
              :hint="trans('general.optional')"
              persistent-hint
              currency="EUR"
              @change="recurringMisc.taxPercent = null"
              :error-messages="validationErrors['tax']"
              :allow-only-negative="true"
            ></currency-input>
          </v-col>
        </v-row>
        <v-btn @click="showTaxGrossFromNetModal = true" elevation="0" small><v-icon small left>mdi-calculator</v-icon>{{ trans('misc.calculateTaxFromNet') }}&hellip;</v-btn>
        <v-row>
          <v-col>
            <v-textarea
              :label="trans('misc.recurring.notes')"
              v-model="recurringMisc.notes"
              outlined
              class="mt-5"
              rows="3"
              hide-details
            ></v-textarea>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider/>
      <v-card-actions>
        <v-spacer></v-spacer>
        <cancel-button @click="showModalInternal = false"></cancel-button>
        <save-button :loading="loading" @click="sendRequest"></save-button>
      </v-card-actions>
    </v-card>

    <tax-gross-from-net-modal
      :show-modal.sync="showTaxGrossFromNetModal"
      @calculation="setAmountsFromNetModal"
      allow-only-negative
    />

  </v-dialog>
</template>

<script>
import CancelButton from './CancelButton'
import {LocaleUtils} from '../utils/locale-utils';
import dayjs from 'dayjs';
import {RouterUtils} from '../utils/router-utils';
import SelectObject from '@/components/SelectObject';
import SelectMiscAccount from '@/components/SelectMiscAccount';
import CurrencyInput from '@/components/CurrencyInput';
import {RecurringMiscApi} from '@/api/recurring-misc-api';
import {MainUtils} from '@/utils/main-utils';
import GlobalValidationErrors from '@/components/GlobalValidationErrors';
import SaveButton from './SaveButton';
import TaxGrossFromNetModal from './TaxGrossFromNetModal';
import TemplatePlaceholderList from './TemplatePlaceholderList';
import DateSelect from './DateSelect';
import {recurringMiscIntervalOptions} from '../lib/recurring-misc-interval-options';

export default {
  name: 'ObjectMiscAdditModal',
  mixins: [
    MainUtils,
    LocaleUtils,
    RouterUtils
  ],
  components: {
    DateSelect,
    TemplatePlaceholderList,
    TaxGrossFromNetModal,
    SaveButton,
    GlobalValidationErrors,
    CurrencyInput,
    SelectMiscAccount,
    SelectObject,
    CancelButton
  },
  props: {
    showModal: {
      type: Boolean
    },
    /** @type {RecurringMiscDto} */
    initialRecurringMisc: {
      type: Object,
      required: false,
    }
  },
  data() {
    return {
      showModalInternal: false,
      loading: false,
      /** @type {RecurringMiscDto} */
      recurringMisc: this.getDefaultMisc(),
      validationErrors: {},
      showTaxGrossFromNetModal: false,
      recurringMiscIntervalOptions: Object.values(recurringMiscIntervalOptions)
    }
  },
  computed: {
    isUpdate() {
      return this.initialRecurringMisc && !!this.initialRecurringMisc.id;
    },
    placeholdersDescriptionTemplate () {
      return [
        {key: 'ObjektName', name: this.trans('object.nameOfObject')},
        {key: 'Jahr', name: this.trans('general.year')},
        {key: 'Monat', name: this.trans('general.month')},
        {key: 'MonatNummer', name: this.trans('general.monthNumber')},
      ]
    }
  },
  methods: {
    getDefaultMisc() {
      let dateFrom = dayjs().startOf('month');

      return {
        objectId: null,
        miscAccountId: null,
        receiptField: null,
        descriptionTemplate: null,
        from: dateFrom.format('YYYY-MM'),
        to: null,
        interval: 'MONTHS_12',
        priceNet: null,
        taxPercent: null,
        price: null,
        tax: null,
        notes: null
      }
    },
    convertMisc(recurringMisc) {
      if (recurringMisc == null) {
        return {};
      }

      let misc = JSON.parse(JSON.stringify(recurringMisc))

      misc.objectId = misc.object ? misc.object.id : null;
      misc.miscAccountId = misc.miscAccount ? misc.miscAccount.id : null;

      return misc;
    },
    sendRequest() {
      if (!this.loading) {
        this.loading = true;

        let request = this.isUpdate ?
          RecurringMiscApi.update(this.initialRecurringMisc.id, this.recurringMisc) :
          RecurringMiscApi.create(this.recurringMisc);

        request.then(() => {
          this.$emit('success');
          this.showModalInternal = false;
        })
        .catch((response) => {
          this.validationErrors = this.getValidationErrors(response, false);
        })
        .finally(() => {
          this.loading = false;
        });
      }
    },

    setAmountsFromNetModal (amounts) {
      this.recurringMisc.price = amounts.gross;
      this.recurringMisc.tax = amounts.tax;
    }
  },
  watch: {
    showModal: {
      immediate: true,
      handler: function (showModal) {
        this.showModalInternal = showModal;
        if (showModal) {
          this.recurringMisc = Object.assign(this.getDefaultMisc(), this.convertMisc(this.initialRecurringMisc));
        }
      }
    },
    showModalInternal(showModalInternal) {
      this.$emit('update:show-modal', showModalInternal);
      this.validationErrors = {};
    }
  }
}
</script>
